import { useCallback, useEffect, useRef, useState } from 'react';
import { aiCandidateAPI, errMsg } from '../../../lib/aiInterviewApi';
import { createLevelMeter, sleep } from '../media';
import { captureAnswer } from './answerCapture';
import { useSessionRecorder } from './useSessionRecorder';

const OVERTIME_END_MS = -150000;

// Orchestrates the voice loop: AI speaks (TTS) -> candidate answers (hands-free capture)
// -> server transcribes + decides the next question -> repeat, until the engine ends.
export function useInterviewSession({ token, ensureStream, streamRef, onFinished, onInterrupted }) {
  const [phase, setPhaseState] = useState('ready');
  const [ai, setAi] = useState(null);
  const [youSaid, setYouSaid] = useState('');
  const [srv, setSrv] = useState(null);
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(false);
  const [typing, setTyping] = useState(false);
  const [speechOn, setSpeechOn] = useState(false);
  const [needTap, setNeedTap] = useState(false);
  const [ttsFailed, setTtsFailed] = useState(false);
  const [started, setStarted] = useState(false);
  const [meter, setMeter] = useState(null);

  const recorder = useSessionRecorder(token);
  const r = useRef({ phase: 'ready', ai: null, srv: null, srvAt: 0, muted: false, typing: false, ended: false, capture: null,
    audio: null, pending: null, aiEndAt: 0, aiRecOffset: null, answerRecOffset: null, lastFailed: null, prompted: false, bgFlag: false });
  const fns = useRef({});

  const setPhase = (p) => { r.current.phase = p; setPhaseState(p); };
  const applyState = (s) => { if (!s) return; r.current.srv = s; r.current.srvAt = Date.now(); setSrv(s); };
  const clock = useCallback(() => {
    const { srv: s, srvAt } = r.current;
    return s ? s.clock_ms + (Date.now() - srvAt) : 0;
  }, []);
  const remaining = useCallback(() => {
    const { srv: s, srvAt } = r.current;
    return s ? s.remaining_ms - (Date.now() - srvAt) : null;
  }, []);

  const play = (url, onPlaying) => new Promise((resolve) => {
    const el = r.current.audio || (r.current.audio = new Audio());
    r.current.pending?.(false);
    r.current.pending = resolve;
    const done = (ok) => { if (r.current.pending === resolve) r.current.pending = null; resolve(ok); };
    el.onended = () => done(true);
    el.onerror = () => done(false);
    el.onplaying = () => onPlaying?.();
    el.src = url;
    el.play().catch((err) => {
      if (err?.name === 'NotAllowedError') setNeedTap(true);
      else if (err?.name !== 'AbortError') done(false);
    });
  });
  const stopAudio = () => {
    r.current.audio?.pause();
    const p = r.current.pending;
    r.current.pending = null;
    p?.(false);
  };
  const abortCapture = () => { r.current.capture?.abort(); r.current.capture = null; };

  const finish = async (reason) => {
    if (r.current.ended) return;
    r.current.ended = true;
    setPhase('ending');
    abortCapture();
    stopAudio();
    await recorder.stop();
    try { await aiCandidateAPI.end(token, reason); } catch (e) { /* already finalised */ }
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    onFinished();
  };

  const listen = () => {
    const c = r.current;
    if (c.ended) return;
    c.answerRecOffset = recorder.offsetMs();
    if (c.muted) return setPhase('muted');
    if (c.typing) return setPhase('typing');
    setSpeechOn(false);
    setPhase('listening');
    c.capture = captureAnswer(streamRef.current, c.meter, {
      onSpeechStart: (at) => {
        setSpeechOn(true);
        const off = recorder.offsetMs();
        c.answerRecOffset = off === null ? null : Math.max(0, off - Math.round(performance.now() - at));
      },
      onNoSpeech: () => {
        if (c.prompted) return;
        c.prompted = true;
        c.capture?.hold(true);
        play(aiCandidateAPI.phraseUrl(token, 'silence')).then(() => c.capture?.hold(false));
      },
      onDone: (blob, info) => { c.capture = null; fns.current.submit({ blob, info }); },
    });
  };

  const speak = async (turn, { replay = false } = {}) => {
    const c = r.current;
    c.ai = turn;
    setAi(turn);
    abortCapture();
    if (!replay) { c.prompted = false; c.aiRecOffset = recorder.offsetMs(); }
    c.bgFlag = false;
    setPhase('thinking');
    const ok = await play(aiCandidateAPI.speechUrl(token, turn.seq), () => setPhase('speaking'));
    if (c.ended) return;
    if (!ok) {
      setTtsFailed(true);
      await sleep(Math.min(9000, 1500 + (turn.text || '').length * 45));
    }
    c.aiEndAt = performance.now();
    if (c.srv?.ended) { finish('completed'); return; }
    listen();
  };

  const submit = async ({ blob, info, typedText, pasted }) => {
    const c = r.current;
    setPhase('thinking');
    const part = recorder.part.current;
    const meta = {
      speech_ms: info?.speechMs || 0,
      latency_ms: info?.speechStartedAt ? Math.max(0, Math.round(info.speechStartedAt - c.aiEndAt)) : 0,
      rec_part: part, rec_offset_ms: c.answerRecOffset ?? recorder.offsetMs(),
      prev_ai_seq: c.ai?.seq, prev_ai_rec_part: part, prev_ai_rec_offset_ms: c.aiRecOffset,
      ...(typedText ? { typed_text: typedText, pasted: !!pasted } : {}),
    };
    const fd = new FormData();
    if (blob) fd.append('audio', blob, `answer.${info?.ext || 'webm'}`);
    fd.append('meta', JSON.stringify(meta));
    for (let i = 0; i < 5; i += 1) {
      try {
        const { data } = await aiCandidateAPI.turn(token, fd);
        applyState(data.state);
        if (data.no_speech) {
          await play(aiCandidateAPI.phraseUrl(token, 'no_speech'));
          return listen();
        }
        setYouSaid(data.candidate.text);
        return speak(data.ai);
      } catch (e) {
        const st = e.response?.status;
        const detail = e.response?.data?.detail || '';
        if (st === 409 && /previous answer/i.test(detail)) { await sleep(2000); continue; }
        if (st === 409) return finish('completed');
        if (!e.response && i < 3) { await sleep(2000 * (i + 1)); continue; }
        c.lastFailed = { blob, info, typedText, pasted };
        setError(errMsg(e, 'We could not send your answer.'));
        return setPhase('send_failed');
      }
    }
    c.lastFailed = { blob, info, typedText, pasted };
    return setPhase('send_failed');
  };

  const join = async () => {
    setError(null);
    setPhase('connecting');
    let stream;
    try { stream = await ensureStream(); } catch (e) { setError('media'); setPhase('error'); return; }
    if (!r.current.meter) { r.current.meter = createLevelMeter(stream); setMeter(r.current.meter); } else r.current.meter.resume();
    if (!r.current.audio) r.current.audio = new Audio();
    try {
      const { data } = await aiCandidateAPI.start(token);
      applyState(data.state);
      recorder.start(stream, data.state?.clock_ms || 0);
      setStarted(true);
      if (data.resumed) await play(aiCandidateAPI.phraseUrl(token, 'resume'));
      speak(data.ai);
    } catch (e) {
      const st = e.response?.status;
      const d = e.response?.data?.detail || '';
      if (st === 425) { setPhase('preparing'); setTimeout(() => fns.current.join(), 4000); return; }
      if (st === 409 && d === 'interrupted') { onInterrupted(); return; }
      if (st === 409 && /already starting/i.test(d)) { setTimeout(() => fns.current.join(), 2500); return; }
      setError(errMsg(e, 'Could not start the interview.'));
      setPhase('error');
    }
  };

  fns.current = { submit, join, listen, speak, finish };

  const controls = {
    join: () => fns.current.join(),
    tapToPlay: () => { setNeedTap(false); r.current.audio?.play().catch(() => r.current.pending?.(false)); },
    toggleMute: () => {
      const track = streamRef.current?.getAudioTracks()[0];
      const next = !r.current.muted;
      if (track) track.enabled = !next;
      r.current.muted = next;
      setMuted(next);
      if (next && r.current.phase === 'listening') { abortCapture(); setPhase('muted'); }
      if (!next && r.current.phase === 'muted') listen();
    },
    repeat: () => { if (r.current.ai && ['listening', 'muted', 'paused', 'typing'].includes(r.current.phase)) speak(r.current.ai, { replay: true }); },
    togglePause: () => {
      if (r.current.phase === 'paused') { stopAudio(); listen(); return; }
      if (r.current.phase !== 'listening') return;
      abortCapture();
      setPhase('paused');
      play(aiCandidateAPI.phraseUrl(token, 'moment'));
    },
    doneAnswering: () => r.current.capture?.stop(),
    toggleTyping: () => {
      const next = !r.current.typing;
      r.current.typing = next;
      setTyping(next);
      if (next && r.current.phase === 'listening') { abortCapture(); setPhase('typing'); }
      if (!next && r.current.phase === 'typing') listen();
    },
    submitTyped: (text, pasted) => submit({ typedText: text, pasted }),
    retrySend: () => { const f = r.current.lastFailed; r.current.lastFailed = null; if (f) submit(f); else listen(); },
    end: () => finish('candidate_ended'),
  };

  // Heartbeat: keeps the session alive and re-syncs the clock.
  useEffect(() => {
    if (!started) return undefined;
    const t = setInterval(async () => {
      if (r.current.ended) return;
      const set = streamRef.current?.getVideoTracks()[0]?.getSettings?.() || {};
      try {
        const { data } = await aiCandidateAPI.heartbeat(token, { fps: set.frameRate ? Math.round(set.frameRate) : null, video_height: set.height || null });
        applyState(data.state);
        if (data.status && !['in_progress', 'starting'].includes(data.status) && !r.current.ended) {
          r.current.ended = true;
          abortCapture();
          stopAudio();
          await recorder.stop();
          onFinished();
        }
      } catch (e) { /* transient */ }
      const rem = remaining();
      if (rem !== null && rem < OVERTIME_END_MS && ['listening', 'paused', 'muted', 'typing'].includes(r.current.phase)) fns.current.finish('completed');
    }, 20000);
    return () => clearInterval(t);
  }, [started]); // eslint-disable-line react-hooks/exhaustive-deps

  // Background voice while the AI is talking (weak, low-severity signal).
  const bgVoice = useRef(0);
  useEffect(() => {
    if (!started) return undefined;
    const t = setInterval(() => {
      const c = r.current;
      if (c.phase !== 'speaking' || !c.meter || c.bgFlag) { bgVoice.current = 0; return; }
      bgVoice.current = c.meter.read() > 0.1 ? bgVoice.current + 250 : 0;
      if (bgVoice.current >= 2500) {
        c.bgFlag = true;
        aiCandidateAPI.signals(token, [{ type: 'background_voice', ts_ms: clock(), details: 'Sustained speech while the AI was talking' }]).catch(() => {});
      }
    }, 250);
    return () => clearInterval(t);
  }, [started, token, clock]);

  useEffect(() => {
    if (!started) return undefined;
    const warn = (e) => { if (!r.current.ended) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [started]);

  useEffect(() => {
    const s = r.current;
    s.ended = false;
    return () => {
      s.ended = true;
      s.capture?.abort();
      s.audio?.pause();
      s.meter?.close();
      recorder.stop();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { phase, ai, youSaid, srv, error, muted, typing, speechOn, needTap, ttsFailed, started, meter, clock, remaining, controls };
}
